import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, mergeMap, switchMap } from 'rxjs/operators';
import * as fromAction from './classroom-course.action';
import { PageList } from '@core/models/page-list';
import { ClassroomCourseApiService } from '@modules/course/classroom/store/services/classroom-course.api.service';
import { AvailableSchedule, ClassroomCourse } from '@modules/course/classroom/models';
import { classroomFeatureKey } from './classroom-course.state';
import { AlertMessageType } from '@modules/shared/enums/alert-message-type';
import { EffectHelperService } from '@modules/core/services/effect-helper.service';

@Injectable()
export class ClassroomCourseEffects {

  fetchClassroomCoursesEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(fromAction.FetchClassroomCourses),
    mergeMap(({ filter }) => {
      return this.classroomCourseApiService.getAllClassroomCourse(filter).pipe(
        map((courses: PageList<ClassroomCourse>) => {
          fromAction.AddTotalCount({ totalCount: courses.total });
          return this.effectHelperService.handleSuccess(fromAction.AddClassroomCourses({ courses: courses.items }))
        }),
        this.effectHelperService.handleError(fromAction.AddError, true, classroomFeatureKey)
      );
    })
  ));


  fetchClassroomCourseEffect$ = createEffect(() => this.actions$.pipe(
    ofType(fromAction.FetchClassroomCourse),
    switchMap(({ courseId }) => {
      return this.classroomCourseApiService
        .getClassroomCourse(courseId)
        .pipe(
          map((course: ClassroomCourse) => this.effectHelperService.handleSuccess(fromAction.AddClassroomCourse({ course }))),
          this.effectHelperService.handleError(fromAction.AddError, true, classroomFeatureKey)
        );
    })
  ));


  fetchAvailableScheduleEffect$ = createEffect(() => this.actions$.pipe(
    ofType(fromAction.FetchClassroomSchedule),
    switchMap(({ courseId }) => {
      return this.classroomCourseApiService
        .getAvailableSchedule(courseId)
        .pipe(
          map((schedules: AvailableSchedule[]) => this.effectHelperService.handleSuccess(fromAction.AddClassroomSchedule({ schedules }))),
          this.effectHelperService.handleError(fromAction.AddError, true, classroomFeatureKey)
        );
    })
  ));

 
  fetchInstructorCoursesEffect$ = createEffect(() => this.actions$.pipe(
    ofType(fromAction.FetchInstructorCourses),
    switchMap(({ instructorId }) => {
      return this.classroomCourseApiService
        .fetchInstructorCourses(instructorId)
        .pipe(
          map((instructorCourses: ClassroomCourse[]) => this.effectHelperService.handleSuccess(fromAction.AddInstructorCourses({ instructorCourses }))),
          this.effectHelperService.handleError(fromAction.AddError, true, classroomFeatureKey)
        );
    })
  ));

  saveCourseReview$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAction.SaveClassroomReview),
      switchMap((payload) =>
        this.classroomCourseApiService
          .sendCourseReview(payload.courseId, payload.review)
          .pipe(
            map((review) => this.effectHelperService.handleSuccess(fromAction.AddCourseReview({ review }), true, 'thanks_for_your_feedback', classroomFeatureKey, AlertMessageType.SUCCESSFUL )),
            this.effectHelperService.handleError(fromAction.AddError, true, classroomFeatureKey)
          )
      )
    )
  );

  saveCourseFeedback$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAction.SaveClassroomFeedback),
      switchMap((payload) =>
        this.classroomCourseApiService
          .sendCourseFeedback(payload.courseId, payload.feedback)
          .pipe(
            map((feedback) => this.effectHelperService.handleSuccess(fromAction.AddCourseFeedback({ feedback }))),
            this.effectHelperService.handleError(fromAction.AddError, true, classroomFeatureKey)
          )
      )
    )
  );

  checkParticipantIdentity$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAction.CheckParticipantIdentity),
      switchMap((payload) =>
        this.classroomCourseApiService
          .checkPartnerIdentity(payload.phone)
          .pipe(
            map((participant) => this.effectHelperService.handleSuccess(fromAction.AddParticipantIdentity({ participant }))),
            this.effectHelperService.handleError(fromAction.AddError, true, classroomFeatureKey)
          )
      )
    )
  );

  decreaseScheduleCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAction.DecreaseScheduleCount),
      switchMap((payload) =>
        this.classroomCourseApiService
          .decreaseScheduleCount(payload.courseId, payload.scheduleId, payload.numOfBooking)
          .pipe(
            map((participant) => this.effectHelperService.handleSuccess(fromAction.Empty())),
            this.effectHelperService.handleError(fromAction.AddError, true, classroomFeatureKey)
          )
      )
    ), { dispatch: false }
  );

  constructor(private actions$: Actions, 
    private classroomCourseApiService: ClassroomCourseApiService, 
    private effectHelperService: EffectHelperService) {}
}
